import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../store';
import * as db from '../lib/db';
import type { ProjectTask, ProjectStatus } from '../types';
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_ORDER } from '../types';
import AttachmentsSection from './AttachmentsSection';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects, getAsset, refresh } = useAppData();
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [newTask, setNewTask] = useState('');
  const [busy, setBusy] = useState(false);

  const project = projects.find((p) => p.id === projectId);

  useEffect(() => {
    if (!project) return;
    db.listProjectTasks(project.id).then(setTasks);
  }, [project?.id]);

  if (!project) return <div className="empty-state">Project not found.</div>;

  const asset = getAsset(project.asset_id);
  const doneCount = tasks.filter((t) => t.done).length;

  async function handleStatusChange(status: ProjectStatus) {
    setBusy(true);
    try {
      await db.updateProject(project!.id, { status });
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function handleAddTask(e: React.FormEvent) {
    e.preventDefault();
    if (!newTask.trim()) return;
    const created = await db.createProjectTask(project!.id, newTask.trim());
    setTasks((prev) => [...prev, created]);
    setNewTask('');
  }

  async function handleToggleTask(task: ProjectTask) {
    const updated = await db.toggleProjectTask(task.id, !task.done);
    setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
  }

  async function handleDeleteTask(task: ProjectTask) {
    await db.deleteProjectTask(task.id);
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
  }

  async function handleDelete() {
    if (!confirm(`Delete "${project!.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await db.deleteProject(project!.id);
      await refresh();
      navigate(asset ? `/assets/${asset.id}` : '/projects');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="project-detail">
      <div className="page-header">
        <h1>{project.name}</h1>
        <select
          className={`status-select status-${project.status}`}
          value={project.status}
          onChange={(e) => handleStatusChange(e.target.value as ProjectStatus)}
          disabled={busy}
        >
          {PROJECT_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {PROJECT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {asset && (
        <Link to={`/assets/${asset.id}`} className="muted">
          {asset.name}
        </Link>
      )}

      <div className="detail-grid card">
        {project.target_timeframe && <Row label="Target" value={project.target_timeframe} />}
        {project.estimated_budget != null && (
          <Row label="Estimated budget" value={`$${project.estimated_budget.toLocaleString()}`} />
        )}
        {project.actual_spent != null && (
          <Row label="Actual spent" value={`$${project.actual_spent.toLocaleString()}`} />
        )}
        {project.notes && <Row label="Notes" value={project.notes} />}
      </div>

      <div className="action-row">
        <Link to={`/projects/${project.id}/edit`} className="btn-secondary">
          Edit
        </Link>
        <button className="btn-secondary danger" onClick={handleDelete} disabled={busy}>
          Delete
        </button>
      </div>

      <section className="checklist-section">
        <div className="section-header">
          <h2>
            Checklist {tasks.length > 0 && `(${doneCount}/${tasks.length})`}
          </h2>
        </div>

        {tasks.length > 0 && (
          <ul className="checklist">
            {tasks.map((task) => (
              <li key={task.id} className={`checklist-item ${task.done ? 'done' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={task.done}
                    onChange={() => handleToggleTask(task)}
                  />
                  <span>{task.text}</span>
                </label>
                <button
                  type="button"
                  className="attachment-delete"
                  onClick={() => handleDeleteTask(task)}
                  aria-label={`Delete ${task.text}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <form className="checklist-add" onSubmit={handleAddTask}>
          <input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a step…"
          />
          <button type="submit" className="btn-secondary small" disabled={!newTask.trim()}>
            + Add
          </button>
        </form>
      </section>

      <AttachmentsSection parent={{ projectId: project.id }} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail-row">
      <span className="muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
